import { css } from 'styled-components';

// How a score series is drawn, in one vocabulary, for both surfaces that draw one.
//
// The geometry was already shared — scoreSeries.js plots the cell's 112x26 box and the panel's
// 640x260 one, and its own comment says a second drawing engine is how a gap ends up drawn one way
// in the table and another way in the panel. Every VISUAL decision was duplicated anyway, under two
// class vocabularies that had already drifted:
//
//   line   .spark-line  w 1.75 op .68     against  .series-line  w 2    op .75
//   gap    .spark-gap   dash 2 2 op .55   against  .gap-tick     dash 3 3 op .75
//   point  .spark-end                     against  .series-dot   w 2    op .9
//
// Nothing failed when only one was edited, and Movement and the history panel are the newest, most
// visible surfaces on this page — so a restyle of the score line (thickness, gap treatment, a
// colour-blind-safe direction encoding) was two style blocks and two vocabularies.
//
// $dense is the 26px cell against the 640px chart. It is a size, not a theme: a 2px stroke in a
// 26px box is a quarter of the plot area, and a 2 2 dash at chart width reads as a solid line. The
// values below are the ones each surface already used, so this commit changes no pixels.
//
// What does NOT belong here is what is genuinely one surface's own: the chart's axes, grid and
// crosshair, and the cell's `here` marker, which exists only because a table row has a current
// occasion and a panel does not.
export const seriesLineCss = css`
  .series-line {
    fill: none;
    stroke: ${({ theme }) => theme.themeColors.text};
    stroke-width: ${({ $dense }) => ($dense ? 1.75 : 2)};
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: ${({ $dense }) => ($dense ? 0.68 : 0.75)};
  }

  /* An unscored occasion. Warning-toned and dashed so it reads as an absence rather than as a
     value — the em dash's equivalent in a channel that has no typography. */
  .series-gap {
    stroke: ${({ theme }) => theme.themeColors.warning};
    stroke-width: 1;
    stroke-dasharray: ${({ $dense }) => ($dense ? '2 2' : '3 3')};
    opacity: ${({ $dense }) => ($dense ? 0.55 : 0.75)};
  }

  /* A plotted occasion. Hollow on the chart, where every occasion carries one and a filled dot
     would read as a second series; filled in the cell, where the only point drawn is the terminal
     one and it carries the direction of the net change. */
  .series-point {
    fill: ${({ theme, $dense }) => ($dense ? theme.themeColors.text : theme.themeColors.componentNoOpacity)};
    stroke: ${({ theme, $dense }) => ($dense ? 'none' : theme.themeColors.text)};
    stroke-width: ${({ $dense }) => ($dense ? 0 : 2)};
    opacity: ${({ $dense }) => ($dense ? 1 : 0.9)};
  }

  /* The direction encoding, which only the cell's terminal point uses today — but it belongs to the
     point vocabulary rather than to the cell, so a chart that ever wants it has it. */
  .series-point[data-token='positive'] {
    fill: ${({ theme }) => theme.themeColors.positive};
  }

  .series-point[data-token='negative'] {
    fill: ${({ theme }) => theme.themeColors.negative};
  }
`;
